import { computed, shallowRef, watch, type Ref } from 'vue'
import type { TableKey, TableRowSelection } from './types'

export function useRowSelection<T>(
  selection: Readonly<Ref<TableRowSelection<T> | undefined>>,
  records: Readonly<Ref<Map<TableKey, T>>>,
  pageRecords: Readonly<Ref<Map<TableKey, T>>>,
  recordKey: (record: T) => TableKey,
  onUpdate: (keys: TableKey[]) => void
) {
  const innerKeys = shallowRef<TableKey[]>(selection.value?.defaultSelectedRowKeys ?? [])
  const cachedRecords = new Map<TableKey, T>()
  const keys = computed(() => (selection.value?.selectedRowKeys ?? innerKeys.value).filter(key => selection.value?.preserveSelectedRowKeys || records.value.has(key)))
  const selectedKeys = computed(() => new Set(keys.value))
  watch([records, keys], () => {
    const selected = new Set(keys.value)
    for (const key of cachedRecords.keys()) if (!selection.value?.preserveSelectedRowKeys || !selected.has(key)) cachedRecords.delete(key)
    for (const [key, record] of records.value) if (selected.has(key)) cachedRecords.set(key, record)
  }, { immediate: true })
  function checkboxProps(record: T) { return selection.value?.getCheckboxProps?.(record) ?? {} }
  const selectableRecords = computed(() => [...pageRecords.value.values()].filter(record => !checkboxProps(record).disabled))
  const isAllSelected = computed(() => selectableRecords.value.length > 0 && selectableRecords.value.every(record => selectedKeys.value.has(recordKey(record))))
  const isIndeterminate = computed(() => !isAllSelected.value && selectableRecords.value.some(record => selectedKeys.value.has(recordKey(record))))
  function selectedRows(next: TableKey[]) {
    return next.map(key => records.value.get(key) ?? cachedRecords.get(key)).filter((record): record is T => record !== undefined)
  }
  function update(next: TableKey[]) {
    innerKeys.value = next
    onUpdate(next)
    const rows = selectedRows(next)
    selection.value?.onChange?.(next, rows)
    return rows
  }
  function toggleRow(record: T, event: Event) {
    if (checkboxProps(record).disabled) return
    const key = recordKey(record)
    const checked = (event.target as HTMLInputElement).checked
    const next = selection.value?.type === 'radio' ? [key] : checked ? [...new Set([...keys.value, key])] : keys.value.filter(value => value !== key)
    const rows = update(next)
    selection.value?.onSelect?.(record, checked, rows, event)
  }
  function toggleAll(event: Event) {
    const checked = (event.target as HTMLInputElement).checked
    const next = new Set(keys.value)
    const changed: T[] = []
    for (const record of selectableRecords.value) {
      const key = recordKey(record)
      if (checked !== next.has(key)) changed.push(record)
      if (checked) next.add(key)
      else next.delete(key)
    }
    const rows = update([...next])
    selection.value?.onSelectAll?.(checked, rows, changed)
  }
  return { selectedKeys, selectableRecords, isAllSelected, isIndeterminate, checkboxProps, toggleRow, toggleAll }
}
